// The connection's 3D scene, built from the calculation state (port of the HTML tool's rebuild3DScene_unione and of
// the scene part of render()). No calculation here: the values compute() produced (theta, b/d/t of the elements,
// lines1, bounds2, the screw grid with x/y/u/ok, d, eta...) become Three.js geometry. Only the screw group is the
// "product" (selectable, solid in x-ray, framed on click); the timber elements are geometric context.
//
// Axes: x, y = the connection plane of the calculation (mm, scaled to metres by MM3D), z = screw axis. Element 2
// (fixed, vertical) behind (z < 0), element 1 (rotated by theta) in front (z > 0); with three members element 3
// (copy of element 2) in front of element 1, with steel/panel on both sides the copy of element 1 behind element 2.
import * as THREE from "three";
import { D2R } from "../../../http/screwConnector/formulas/angles";
import { elem1Trig } from "../../../http/screwConnector/formulas/grid";
import {
  createWoodMaterialFactory,
  disposeObjectTree,
  linearColor,
  type DimCommand,
  type DimLabelCommand,
  type DimLineOptions,
  type SideFrame,
  type TC3DViewer,
  type ViewerUserData,
  type WoodMaterialOptions,
} from "./tc3dViewer";

export const MM3D = 0.001; // the calculation is in mm, the scene in metres

// Wood-based panels (OSB, plywood, particleboard): darker than the solid/glulam members; same colour in the legend.
export const PANEL_WOOD_HEX = 0xb5834a;
export const PANEL_WOOD_CSS = "#b5834a";
export const PANEL_WOOD_BORDER = "#7d5528";

const WOOD2_HEX = 0xe0c799; // element 2 (and 3), CLT 0° layers
const WOOD2_CROSS_HEX = 0xb99a5f; // element 2 CLT 90° layers
const WOOD1_HEX = 0xefd8a8; // element 1, CLT 0° layers
const WOOD1_CROSS_HEX = 0xcfae6e; // element 1 CLT 90° layers

// Display of a timber element chosen in the 3D view's Elements menu.
export type ElementMode = "solid" | "ghost" | "hidden";

// Opacity of a displayed element: semi-transparent lets the screws through; with "Mostra quote" the elements turn
// semi-transparent too, so the dimension labels stay readable. Same rule for the interactive view and the report.
export function elementOpacity(mode: ElementMode, showQuote: boolean): number {
  return mode === "ghost" ? 0.35 : showQuote ? 0.45 : 1;
}

export type ScenePoint = { u: number; x: number; y: number; ok: boolean };
export type SceneLine = { C: number };
export type SceneClt = {
  latoInserimento: string;
  spessoreTotale: number;
  strati: ReadonlyArray<{ spessore: number; orientamento: number }>;
};

// The part of the engine's CalcState (HTML compute() return value, same field names) the scene reads.
export type SceneState = {
  theta: number;
  b1: number;
  d1: number;
  b2: number;
  d2: number;
  t1: number;
  t2: number;
  d: number;
  lines1: { continuo1: boolean; retta1: SceneLine; rettaN: SceneLine; retta3: SceneLine };
  bounds2: { continuo2: boolean; xMin: number; xMax: number; yMin: number };
  grid: ReadonlyArray<ScenePoint>;
  gridB?: ReadonlyArray<ScenePoint> | null;
  fnKN: number;
  fvKN: number;
  faKN: number;
  clt1: SceneClt | null;
  clt2: SceneClt | null;
  materiale1: string;
  three: boolean;
  threeD: boolean;
  steel3: boolean;
  panel1: boolean;
  screwLEff?: number | null;
  // scene only: blocked calculation, elements without screws
  hideScrews?: boolean;
};

// Where the scene builder puts its parts. The imperative viewer (report captures) adds them to its own group; the
// react-three-fiber scene keeps the two elements, the screws and the forces separate so the user can show/hide them
// independently. Element 3 goes with the element it copies (element 2 with three members, element 1 with steel3).
// Dimensions and labels are collected as commands, since the two renderers draw them with their own technology.
export type SceneSink = {
  element1: THREE.Group;
  element2: THREE.Group;
  screws: THREE.Group;
  forces: THREE.Group;
  woodMaterial(colorHex: number, opts?: WoodMaterialOptions): THREE.Material;
  addDimLine(
    p1: THREE.Vector3,
    p2: THREE.Vector3,
    offsetDir: THREE.Vector3,
    offsetAmt: number,
    label: string,
    opts?: DimLineOptions,
  ): void;
  addLabel(x: number, y: number, z: number, text: string, force?: DimLabelCommand["force"]): void;
  setSideFrame(frame: SideFrame | null): void;
};

// Adapter that lets the existing report-capture viewer build this scene. The report images show no forces (as the
// lock calculator's): their arrows stay out of the viewer's group (never rendered nor framed) and their labels are
// dropped.
export function viewerSceneSink(v: TC3DViewer): SceneSink {
  const element1 = new THREE.Group();
  const element2 = new THREE.Group();
  const screws = new THREE.Group();
  const forces = new THREE.Group();
  v.group.add(element2, element1, screws);
  return {
    element1,
    element2,
    screws,
    forces,
    woodMaterial: (colorHex, opts) => v.woodMaterial(colorHex, opts),
    addDimLine: (p1, p2, offsetDir, offsetAmt, label, opts) => v.addDimLine(p1, p2, offsetDir, offsetAmt, label, opts),
    addLabel: (x, y, z, text, force) => {
      if (!force) v.addLabel(x, y, z, text);
    },
    setSideFrame: (frame) => {
      v.sideFrame = frame;
    },
  };
}

// The scene is always built whole: the Elements menu is a setting of the interactive view, applied to the built groups,
// and the report shows the whole connection.
export type UpdateSceneOptions = {
  showQuote: boolean;
  blocked?: boolean;
};

// What render() used to do with the state: a blocked calculation shows only the elements (no screws, fallback d for
// the scene margins); empty geometric inputs (NaN while typing) produce no scene, since
// NaN coordinates would generate invalid geometry. A continuous element has no length (its d1/d2 field is hidden and
// may be empty): it is not required. The report viewer leaves its previous frame when this returns null; the R3F
// wrapper keeps the last valid build on screen.
function prepareSceneState(state: SceneState, blocked: boolean): SceneState | null {
  const sceneState: SceneState = blocked
    ? { ...state, grid: [], hideScrews: true, d: Number.isFinite(state.d) ? state.d : 6 }
    : state;
  const needs = [
    sceneState.d,
    sceneState.theta,
    sceneState.b1,
    ...(sceneState.lines1?.continuo1 ? [] : [sceneState.d1]),
    sceneState.b2,
    ...(sceneState.bounds2?.continuo2 ? [] : [sceneState.d2]),
    sceneState.t1,
    sceneState.t2,
    sceneState.fnKN,
    sceneState.fvKN,
  ];
  if (blocked && sceneState.lines1 && sceneState.bounds2) {
    needs.push(
      sceneState.lines1.retta1.C,
      sceneState.lines1.rettaN.C,
      sceneState.lines1.retta3.C,
      sceneState.bounds2.xMin,
      sceneState.bounds2.xMax,
      sceneState.bounds2.yMin,
    );
  }
  return needs.every((n) => Number.isFinite(n)) ? sceneState : null;
}

export function updateScene(v: TC3DViewer, state: SceneState | null, opts: UpdateSceneOptions): void {
  if (!state) {
    v.clearGroup();
    v.refit();
    return;
  }
  const blocked = !!opts.blocked;
  const sceneState = prepareSceneState(state, blocked);
  if (sceneState) {
    v.clearGroup();
    v.sideFrame = null;
    rebuild3DScene(viewerSceneSink(v), sceneState, opts);
    v.refit();
  } else if (blocked) {
    v.clearGroup();
    v.refit();
  }
}

export type BuiltScrewScene = {
  // the state the scene was built from (blocked: no screws) and where its elements are
  state: SceneState;
  layout: SceneLayout;
  root: THREE.Group;
  element1: THREE.Group;
  element2: THREE.Group;
  screws: THREE.Group;
  forces: THREE.Group;
  dims: DimCommand[];
  sideFrame: SideFrame | null;
  // the whole scene's geometry
  bounds: THREE.Box3;
  dispose(): void;
};

// The same scene data the report-capture viewer builds, but as independent groups for the react-three-fiber viewer.
// The caller owns the result and must call dispose() when replacing it (geometries, materials and grain textures).
export function buildScrewScene(state: SceneState, opts: UpdateSceneOptions): BuiltScrewScene | null {
  const sceneState = prepareSceneState(state, !!opts.blocked);
  if (!sceneState) return null;
  const root = new THREE.Group();
  const element1 = new THREE.Group();
  const element2 = new THREE.Group();
  const screws = new THREE.Group();
  const forces = new THREE.Group();
  root.add(element2, element1, screws, forces);
  const dims: DimCommand[] = [];
  let sideFrame: SideFrame | null = null;
  const woodMaterials = createWoodMaterialFactory();
  const sink: SceneSink = {
    element1,
    element2,
    screws,
    forces,
    woodMaterial: woodMaterials.woodMaterial,
    addDimLine: (p1, p2, offsetDir, offsetAmt, label, lineOpts) =>
      dims.push({ kind: "line", p1, p2, offsetDir, offsetAmt, label, opts: lineOpts }),
    addLabel: (x, y, z, text, force) => dims.push({ kind: "label", x, y, z, text, force }),
    setSideFrame: (frame) => {
      sideFrame = frame;
    },
  };
  rebuild3DScene(sink, sceneState, opts);
  const bounds = new THREE.Box3().setFromObject(root);
  return {
    state: sceneState,
    layout: sceneLayout(sceneState),
    root,
    element1,
    element2,
    screws,
    forces,
    dims,
    sideFrame,
    bounds,
    dispose() {
      disposeObjectTree(root);
      woodMaterials.dispose();
    },
  };
}

// Where the elements are, in the builder's units: thicknesses along z in metres, the scenic extent of element 2 along
// y (yBot..yTop) and of element 1 along its axis u (uNear..uFar) in mm. Shared by the builder and the interactive
// viewer's dimensions.
export type SceneLayout = {
  c: number;
  s: number;
  clt1Wide: SceneClt | null;
  clt2Wide: SceneClt | null;
  T1: number;
  T2: number;
  yTop: number;
  yBot: number;
  uNear: number;
  uFar: number;
};

export function sceneLayout(state: SceneState): SceneLayout {
  const { theta, b1, d1, b2, d2, t1, t2, lines1, bounds2, grid, d, clt1, clt2 } = state;
  const { c, s } = elem1Trig(theta);
  // CLT inserted on the wide face (screw across the layers): drawn layer by layer with the real layer thicknesses.
  // Narrow face (screw parallel to the panel): the layers are in the panel plane, drawn as a single block.
  const clt1Wide = clt1 && clt1.latoInserimento === "wide" ? clt1 : null;
  const clt2Wide = clt2 && clt2.latoInserimento === "wide" ? clt2 : null;

  // Margin beyond the outermost screw and real extent of the grid: the scenic length of the "infinite" sides (side
  // of F for element 1, upper side for element 2, both ends of a continuous element) grows to always contain the grid
  // (an offset grid would otherwise look floating). The real ends (d1/d2) stay as input: a screw beyond them is red.
  const gridMargin = Math.max(d * 3, 30);
  const gridUs = grid.map((p) => p.u);
  const gridYs = grid.map((p) => p.y);
  // element 2 (fixed, vertical): "infinite" upper side cut at a scenic length (d2 only if not continuous)
  const yTopBase = Math.max(bounds2.continuo2 ? 0 : d2, b1, b2) * 0.9;
  // element 1 (rotated by theta): local axis u along (-s, c), side towards F ("infinite") cut at uFar (d1 only if not
  // continuous)
  const uFarBase = Math.max(b2 * 0.8, lines1.continuo1 ? 0 : d1 * 0.45);
  return {
    c,
    s,
    clt1Wide,
    clt2Wide,
    T1: Math.max(clt1Wide ? clt1Wide.spessoreTotale : t1, 1) * MM3D,
    T2: Math.max(clt2Wide ? clt2Wide.spessoreTotale : t2, 1) * MM3D,
    yTop: bounds2.continuo2 ? Math.max(yTopBase, Math.max(...gridYs) + gridMargin) : Math.max(d2 * 0.5, Math.max(...gridYs, 0) + 10),
    yBot: bounds2.continuo2 ? -Math.max(yTopBase, -Math.min(...gridYs) + gridMargin) : -d2,
    uNear: lines1.continuo1 ? -Math.max(uFarBase, -Math.min(...gridUs) + gridMargin) : -d1,
    uFar: lines1.continuo1 ? Math.max(uFarBase, Math.max(...gridUs) + gridMargin) : Math.max(d1 * 0.5, Math.max(...gridUs, 0) + 10),
  };
}

// rebuild3DScene_unione
export function rebuild3DScene(sink: SceneSink, state: SceneState, opts: UpdateSceneOptions): void {
  const { theta, b1, d1, b2, d2, t1, t2, lines1, bounds2, grid, d } = state;
  const { fnKN, fvKN, faKN } = state;
  const { materiale1 } = state;
  const isSteel1 = materiale1 === "acciaio";
  const { c, s, clt1Wide, clt2Wide, T1, T2, yTop, yBot, uNear, uFar } = sceneLayout(state);
  const zC1 = T1 / 2;
  const zC2 = -T2 / 2;

  // With "Mostra quote" the elements turn semi-transparent so the dimension labels stay readable.
  function applyQuoteTransparency(mat: THREE.Material): void {
    mat.opacity = elementOpacity("solid", opts.showQuote);
    mat.transparent = mat.opacity < 1;
  }
  // Steel element 1: a plate, flat metallic grey (no grain, no CLT).
  function steelPlateMaterial(): THREE.MeshStandardMaterial {
    return new THREE.MeshStandardMaterial({ color: linearColor(0xd5d5d5), metalness: 0.6, roughness: 0.35 });
  }
  function addBox(
    element: THREE.Group,
    sx: number,
    sy: number,
    sz: number,
    material: THREE.Material,
    x: number,
    y: number,
    z: number,
  ) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), material);
    applyQuoteTransparency(material);
    m.position.set(x, y, z);
    element.add(m);
    return m;
  }

  // ---- element 2 (timber, fixed, vertical), between yBot and yTop (sceneLayout).
  const midY2 = ((yTop + yBot) / 2) * MM3D;
  const len2 = (yTop - yBot) * MM3D;
  if (clt2Wide) {
    // layers from the outer face (z = -T2) to the face in contact with element 1 (z = 0); 0° and 90° layers with
    // vertical/horizontal grain and a lighter/darker tint
    let zCursor2 = -T2;
    clt2Wide.strati.forEach((layer) => {
      const h = Math.max(layer.spessore, 1) * MM3D;
      const mat = sink.woodMaterial(layer.orientamento === 0 ? WOOD2_HEX : WOOD2_CROSS_HEX, {
        vertical: layer.orientamento === 0,
      });
      addBox(sink.element2, b2 * MM3D, len2, h, mat, 0, midY2, zCursor2 + h / 2);
      zCursor2 += h;
    });
  } else {
    addBox(sink.element2, b2 * MM3D, len2, T2, sink.woodMaterial(WOOD2_HEX), 0, midY2, zC2);
  }

  // ---- element 3 (three members only): identical copy of element 2 on the other face of element 1 (z from T1 to T1+T2).
  if (state.three) {
    if (clt2Wide) {
      // layers from the outer face (z = T1+T2) towards element 1 (z = T1): mirrored order
      let zCursor3 = T1 + T2;
      clt2Wide.strati.forEach((layer) => {
        const h = Math.max(layer.spessore, 1) * MM3D;
        const mat = sink.woodMaterial(layer.orientamento === 0 ? WOOD2_HEX : WOOD2_CROSS_HEX, {
          vertical: layer.orientamento === 0,
        });
        addBox(sink.element2, b2 * MM3D, len2, h, mat, 0, midY2, zCursor3 - h / 2);
        zCursor3 -= h;
      });
    } else {
      addBox(sink.element2, b2 * MM3D, len2, T2, sink.woodMaterial(WOOD2_HEX), 0, midY2, T1 + T2 / 2);
    }
  }

  // ---- element 1 (rotated by theta), between uNear and uFar along its axis u (sceneLayout).
  const uMid = (uFar + uNear) / 2;
  const x1 = -uMid * s * MM3D;
  const y1 = uMid * c * MM3D;
  const rotZ1 = Math.atan2(c, -s);
  const len1 = (uFar - uNear) * MM3D;
  if (clt1Wide) {
    // from the face in contact with element 2 (z = 0) to the outer face (z = T1)
    let zCursor1 = 0;
    clt1Wide.strati.forEach((layer) => {
      const h = Math.max(layer.spessore, 1) * MM3D;
      const mat = sink.woodMaterial(layer.orientamento === 0 ? WOOD1_HEX : WOOD1_CROSS_HEX, {
        vertical: layer.orientamento === 0,
      });
      addBox(sink.element1, len1, b1 * MM3D, h, mat, x1, y1, zCursor1 + h / 2).rotation.z = rotZ1;
      zCursor1 += h;
    });
  } else {
    const mat1 = isSteel1
      ? steelPlateMaterial()
      : sink.woodMaterial(state.panel1 ? PANEL_WOOD_HEX : WOOD1_HEX, { vertical: false });
    addBox(sink.element1, len1, b1 * MM3D, T1, mat1, x1, y1, zC1).rotation.z = rotZ1;
    if (state.steel3) {
      // element 3: copy of element 1 (plate or panel) on the other face of the central timber (z from -T2-T1 to -T2)
      const mat1b = state.panel1 ? sink.woodMaterial(PANEL_WOOD_HEX, { vertical: false }) : steelPlateMaterial();
      addBox(sink.element1, len1, b1 * MM3D, T1, mat1b, x1, y1, -T2 - T1 / 2).rotation.z = rotZ1;
    }
  }

  // ---- screws: the PRODUCT, the group the report frames (tagged userData.type: the report viewer's frameProductNow
  // looks for tagged direct children of its root). Insertion axis orthogonal to the shear plane (z).
  if (!state.hideScrews) {
    const screws = sink.screws;
    screws.userData = { type: "viti" } satisfies ViewerUserData;
    const rS = (Math.max(d, 1) * MM3D) / 2;
    // With a known screw the real length (L - washer h) is drawn from the head on the outer face; without one, the
    // screw spans the whole thickness. Three members: two mirrored groups, heads on the outer faces of elements 2
    // and 3, tips in the central element 1.
    const Lreal =
      state.screwLEff != null && Number.isFinite(state.screwLEff) && state.screwLEff > 0
        ? state.screwLEff * MM3D
        : null;
    // zFace = outer face the screw enters from, dz = driving direction along z (-1 towards -z, +1 towards +z)
    const addScrewGroup = (zFace: number, dz: number, gridUse: ReadonlyArray<ScenePoint>) => {
      const lenS =
        (Lreal != null
          ? Lreal
          : state.threeD
            ? T1 + 2 * T2
            : state.three
              ? T2 + T1 / 2
              : state.steel3
                ? T1 + T2 / 2
                : T1 + T2) + 0.006;
      const zh = zFace - dz * 0.006; // head just outside the face
      const zS = zh + (dz * lenS) / 2;
      gridUse.forEach((p) => {
        // gunmetal for admissible screws, red (status colour) for the others
        const mat = new THREE.MeshStandardMaterial({
          color: linearColor(p.ok ? 0x2c3539 : 0x8e1f1f),
          roughness: 0.35,
          metalness: 0.7,
        });
        if (Lreal != null) {
          // shank up to tipLen from the end, then a cone pointing in the driving direction
          const tipLen = Math.min(3 * Math.max(d, 1) * MM3D, Lreal * 0.3);
          const shaftLen = lenS - tipLen;
          const mesh = new THREE.Mesh(new THREE.CylinderGeometry(rS, rS, shaftLen, 12), mat);
          mesh.rotation.x = Math.PI / 2;
          mesh.position.set(p.x * MM3D, p.y * MM3D, zh + (dz * shaftLen) / 2);
          screws.add(mesh);
          const tip = new THREE.Mesh(new THREE.ConeGeometry(rS, tipLen, 12), mat);
          tip.rotation.x = dz < 0 ? -Math.PI / 2 : Math.PI / 2;
          tip.position.set(p.x * MM3D, p.y * MM3D, zFace + dz * Lreal - (dz * tipLen) / 2);
          screws.add(tip);
        } else {
          const mesh = new THREE.Mesh(new THREE.CylinderGeometry(rS, rS, lenS, 12), mat);
          mesh.rotation.x = Math.PI / 2; // cylinder axis along z
          mesh.position.set(p.x * MM3D, p.y * MM3D, zS);
          screws.add(mesh);
        }
      });
    };
    // double shear: one screw per point, from the outer face of element 2 through 1 into 3
    if (state.threeD) addScrewGroup(-T2, +1, grid);
    else if (state.three) {
      addScrewGroup(-T2, +1, grid);
      addScrewGroup(T1 + T2, -1, state.gridB || grid);
    } else if (state.steel3) {
      // heads on the two plates, tips in the central timber
      addScrewGroup(T1, -1, grid);
      addScrewGroup(-T2 - T1, +1, state.gridB || grid);
    } else addScrewGroup(T1, -1, grid);
  }

  // ---- input forces: Fn along the element 1 axis and Fv perpendicular to it, both from the axes' crossing, in the
  // shear plane; Fa along the screw axis (z). Same scale (mm per kN) for all; always shown, regardless of "Mostra quote".
  {
    const dirX = -s;
    const dirY = c; // element 1 axis, Fn direction
    const perpX = c;
    const perpY = s; // perpendicular, Fv direction
    const R = Math.hypot(fnKN, fvKN);
    const refLen = Math.max(b1, b2, 100) * 0.7; // scenic reference length (not d1: valid with continuous element 1 too)
    const forceScale = refLen / Math.max(R, Math.abs(faKN), 1e-6);
    const zF = T1 + (state.three ? T2 : 0) + 0.05; // in front of every element
    const UP = new THREE.Vector3(0, 1, 0);
    const arrow3D = (from: THREE.Vector3, to: THREE.Vector3, color: number, radius: number) => {
      const diff = new THREE.Vector3().subVectors(to, from);
      const len = diff.length();
      if (len < 1e-6) return;
      const dir = diff.clone().normalize();
      const headLen = Math.min(len * 0.3, 0.045);
      const shaftLen = Math.max(len - headLen, len * 0.55);
      const mat = new THREE.MeshBasicMaterial({ color: linearColor(color) });
      const quat = new THREE.Quaternion().setFromUnitVectors(UP, dir);
      const shaft = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, shaftLen, 8), mat);
      shaft.position.copy(from).addScaledVector(dir, shaftLen / 2);
      shaft.quaternion.copy(quat);
      const head = new THREE.Mesh(new THREE.ConeGeometry(radius * 2.6, headLen, 10), mat);
      head.position.copy(from).addScaledVector(dir, shaftLen + headLen / 2);
      head.quaternion.copy(quat);
      sink.forces.add(shaft, head);
    };
    const rComp = Math.max(refLen * MM3D * 0.01, 0.0025);
    const cAmber = 0x8a6d1a;
    const cViola = 0x4a3c7a;
    const cRed = 0x8e1f1f;
    // each label just beyond the tip of its arrow, never on the shaft
    const forceLabel = (from: THREE.Vector3, to: THREE.Vector3, text: string) => {
      const dir = new THREE.Vector3().subVectors(to, from).normalize();
      const at = to.clone().addScaledVector(dir, 0.015);
      sink.addLabel(at.x, at.y, at.z, text, { away: dir });
    };

    if (R > 1e-9) {
      const p1x = fnKN * forceScale * dirX;
      const p1y = fnKN * forceScale * dirY; // tip of Fn
      const pvx = fvKN * forceScale * perpX;
      const pvy = fvKN * forceScale * perpY; // tip of Fv
      const O = new THREE.Vector3(0, 0, zF);
      const P1 = new THREE.Vector3(p1x * MM3D, p1y * MM3D, zF);
      const PV = new THREE.Vector3(pvx * MM3D, pvy * MM3D, zF);
      if (Math.abs(fnKN) > 1e-9) {
        arrow3D(O, P1, cAmber, rComp);
        forceLabel(O, P1, "Fn=" + fnKN.toFixed(1) + "kN");
      }
      if (Math.abs(fvKN) > 1e-9) {
        arrow3D(O, PV, cViola, rComp);
        forceLabel(O, PV, "Fv=" + fvKN.toFixed(1) + "kN");
      }
    }

    // Fa (withdrawal, positive = pull-out): from the outer face of the connection outwards, not hidden in the timber
    if (Math.abs(faKN) > 1e-9) {
      const zLen = Math.abs(faKN) * forceScale * MM3D;
      const from = new THREE.Vector3(0, 0, zF);
      const to = new THREE.Vector3(0, 0, zF + Math.sign(faKN) * zLen);
      arrow3D(from, to, cRed, rComp * 1.3);
      forceLabel(from, to, "Fa=" + faKN.toFixed(1) + "kN");
    }
  }

  // ---- t1/t2 thickness dimensions for the report SIDE view only (opts.only = 'side'), at the screws' height, chained
  // above each element's thickness; plus the side view frame (near-orthographic projection).
  {
    const okPts = (grid || []).filter((p) => p.ok);
    if (okPts.length) {
      const ys = okPts.map((p) => p.y);
      const xs = okPts.map((p) => p.x);
      const yMax = Math.max(...ys);
      const yMin = Math.min(...ys);
      const xm = xs.reduce((a, b) => a + b, 0) / xs.length;
      const yS = yMax + 0.45 * Math.max(yMax - yMin, 60);
      const pz = (z: number) => new THREE.Vector3(xm * MM3D, yS * MM3D, z);
      const zero0 = new THREE.Vector3(0, 0, 0);
      sink.addDimLine(pz(-T2), pz(0), zero0, 0, "t2=" + t2.toFixed(0), { light: true, only: "side" });
      sink.addDimLine(pz(0), pz(T1), zero0, 0, "t1=" + t1.toFixed(0), { light: true, only: "side" });
      if (state.three) sink.addDimLine(pz(T1), pz(T1 + T2), zero0, 0, "t3=" + t2.toFixed(0), { light: true, only: "side" });
      if (state.steel3) {
        sink.addDimLine(pz(-T2 - T1), pz(-T2), zero0, 0, "t3=" + t1.toFixed(0), { light: true, only: "side" });
      }
      // all the thicknesses horizontally (z), from the screws to the dimension line vertically
      const mrg = Math.max(d * 3, 25);
      sink.setSideFrame({
        cx: xm * MM3D,
        cy: ((yMin + yS) / 2) * MM3D,
        cz: state.three ? T1 / 2 : state.steel3 ? -T2 / 2 : (T1 - T2) / 2,
        halfW: ((state.three ? T1 + 2 * T2 : state.steel3 ? 2 * T1 + T2 : T1 + T2) / 2) * 1.18 + 0.02,
        halfH: ((yS - yMin) / 2 + mrg) * MM3D,
      });
    }
  }

  // ---- dimensions (same labels as the old 2D drawing) on the front plane of element 1. Hidden by unchecking
  // "Mostra quote".
  if (opts.showQuote) {
    const zQ = T1 + (state.three ? T2 : 0) + 0.02;
    const zero = new THREE.Vector3(0, 0, 0);
    const P = (x: number, y: number) => new THREE.Vector3(x * MM3D, y * MM3D, zQ);
    const toXY = (u: number, w: number): [number, number] => [-u * s + w * c, u * c + w * s]; // (u,v) element 1 -> (x,y)
    const PU = (u: number, w: number) => {
      const [x, y] = toXY(u, w);
      return P(x, y);
    };
    // "light" dimension (solid line + 45° tick instead of arrows, ISO 129): arrows would be too heavy over the
    // semi-transparent timber. `outer`: the side the label goes to (none: the renderer's default)
    const dim = (p1: THREE.Vector3, p2: THREE.Vector3, label: string, outer = zero) =>
      sink.addDimLine(p1, p2, outer, 0, label, { light: true });

    // width b2 - across element 2, just above its base (on the reference plane)
    const basefY = yBot + 10;
    dim(P(-b2 / 2, basefY), P(b2 / 2, basefY), "b2=" + b2.toFixed(0));

    // width b1 - across element 1, just inside its near end
    const basefU = uNear + 15;
    dim(PU(basefU, -b1 / 2), PU(basefU, b1 / 2), "b1=" + b1.toFixed(0));

    // thicknesses (along z) - out of the elements, on their outer side (the left): on an edge of the timber the
    // elements would hide them as soon as the view turns. Element 3 is measured with the element it copies.
    const tOut = 60; // mm from the element, beyond the d2 dimension
    // mm higher than the element's vertex: the base of element 2 lies on the reference plane, which would cut the label
    const tUp = 40;
    // t2 (and t3 with three members) - left of the bottom-left vertex of element 2 (z ∈ [-T2, 0])
    const left = new THREE.Vector3(-1, 0, 0);
    const Z2 = (z: number) => new THREE.Vector3((-b2 / 2 - tOut) * MM3D, (yBot + tUp) * MM3D, z);
    dim(Z2(-T2), Z2(0), "t2=" + t2.toFixed(0), left);
    if (state.three) dim(Z2(T1), Z2(T1 + T2), "t3=" + t2.toFixed(0), left);
    // t1 (and t3 with steel/panel on both sides) - beyond the near end of element 1 (z ∈ [0, T1]), which is on the
    // left whatever θ: outwards along -u
    const beyondEnd1 = new THREE.Vector3(s, -c, 0);
    const end1 = PU(uNear - tOut, -b1 / 2);
    const Z1 = (z: number) => new THREE.Vector3(end1.x, end1.y + tUp * MM3D, z);
    dim(Z1(0), Z1(T1), "t1=" + t1.toFixed(0), beyondEnd1);
    if (state.steel3) dim(Z1(-T2 - T1), Z1(-T2), "t3=" + t1.toFixed(0), beyondEnd1);

    // d2 (length of element 2, when not continuous) - left of element 2, along its whole drawn length
    if (!bounds2.continuo2) {
      dim(
        new THREE.Vector3((-b2 / 2 - 30) * MM3D, yBot * MM3D, 0),
        new THREE.Vector3((-b2 / 2 - 30) * MM3D, yTop * MM3D, 0),
        "d2=" + d2.toFixed(0),
      );
    }

    // d1 (length of element 1, when not continuous) - beside element 1, along its whole drawn length
    if (!lines1.continuo1) {
      dim(PU(uNear, -b1 / 2 - 25), PU(uFar, -b1 / 2 - 25), "d1=" + d1.toFixed(0));
    }

    // angle theta
    const rTheta = Math.max(b1, b2) * 0.7;
    const thm = (theta / 2) * D2R;
    sink.addLabel(Math.sin(thm) * rTheta * MM3D, -Math.cos(thm) * rTheta * MM3D, zQ, "θ=" + theta.toFixed(0) + "°");
  }
}
